// Import polyfills first for older browser compatibility
import './polyfills.js';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { ErrorBoundary } from './ErrorBoundary';
import { initPostHog } from './lib/posthog';
import { initPwaAnalytics, superPropsPwa } from './lib/pwaAnalytics';
import { iniciarSeguimientoUltimoLogin } from './lib/ultimoLogin';
import { ensureMetaParams } from './lib/metaParams';
import { capturarNotificacionDelPush } from './lib/notificaciones';
import './index.css';
import './shine.css';
// Mascota del chat (Cumbre) — anima propiedades geométricas de SVG, así que no puede
// vivir en Tailwind. Ver `components/CumbreMascot.tsx`.
import './cumbre.css';
import './fonts';
import { iniciarBranding } from './branding';
import { iniciarAvisoAlVolver } from './lib/avisoDeSaldo';

// Meta: asegura _fbc/_fbp lo antes posible en el journey (best practice del
// Parameter Builder: capturar al cargar, no recién en el signup) — cubre el
// caso píxel bloqueado con fbclid presente en la URL de entrada.
ensureMetaParams();
// Branding de cliente (marca blanca): toma `?branding=` y lo recordado en el dispositivo y
// lo aplica antes del primer render; con la sesión resuelta, `ProtectedRoute` aplica lo
// que diga la empresa. El script inline de index.html ya pintó el snapshot antes de esto.
// Ver ADR-2026-10-06-branding-por-cliente.
iniciarBranding();
// Si la app se abrió tocando un push, el id de la notificación viene en la URL. Se levanta ACÁ,
// antes de renderizar, por dos razones: el parámetro se saca de la barra de direcciones antes de
// que el usuario lo vea, y queda anotado aunque todavía no haya sesión para marcarlo (de eso se
// encarga el hook de la campana cuando la sesión existe).
capturarNotificacionDelPush();
// El contador de créditos relee el saldo al volver a la pestaña tras un rato afuera: lo que
// cambió en otro lado (un compañero que generó, un pago acreditado) aparece sin recargar.
// Ver ADR-2026-10-29-el-contador-de-creditos-se-entera-solo.
iniciarAvisoAlVolver();

// Fallback: enforce notranslate attributes at runtime (some translators ignore meta on initial load)
try {
  document.documentElement.setAttribute('translate', 'no');
  document.documentElement.classList.add('notranslate');
  document.body?.setAttribute('translate', 'no');
} catch {}

// Service worker. En PRODUCCIÓN lo registramos (PWA installability). En DEV
// lo DESREGISTRAMOS y borramos todos los caches: un SW viejo de un build
// anterior (que sí cacheaba) puede seguir sirviendo assets cacheados y hacer
// que NO se vean los cambios aunque el dev server sirva código nuevo.
if ('serviceWorker' in navigator) {
  if (import.meta.env.DEV) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => {});
    if ('caches' in window) {
      caches
        .keys()
        .then((keys) => keys.forEach((k) => caches.delete(k)))
        .catch(() => {});
    }
  } else {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    });
  }
}

// Capture beforeinstallprompt early — the event fires once at page load
// and is lost if no listener exists. Store it globally for the React hook.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  (window as any).__pwaInstallPrompt = e;
});

// Analytics. Carga el SDK en un chunk aparte (no bloquea el primer render) y
// en dev no hace nada. Arranca antes del render para no perder el pageview
// inicial ni los errores tempranos.
//
// Las super props de PWA van COMO ARGUMENTO (no con un `register` posterior)
// para que el pageview inicial ya salga etiquetado con si la app corre
// instalada o en el navegador. Ver ADR-2026-08-04-medicion-instalacion-pwa.
initPostHog(superPropsPwa());
initPwaAnalytics();

// "Último utilizado" de la pantalla de login: anota con qué método se ingresó. Va acá,
// fuera de React, porque el login ocurre en `/auth` (ruta pública) y un listener que
// viva dentro de un componente de ruta protegida todavía no existe cuando dispara el
// `SIGNED_IN`. Ver ADR-2026-08-11-ultimo-metodo-de-ingreso-se-registra-global.
iniciarSeguimientoUltimoLogin();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
);